// @ts-nocheck
import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, MessageCircle, Sparkles, Heart } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";

export function Footer() {
  const [channels, setChannels] = useState<Record<string, string>>({});

  useEffect(() => {
    supabase.from("social_media_channels").select("platform, url").then(({ data }) => {
      const map: Record<string, string> = {};
      data?.forEach((ch: any) => { map[ch.platform] = ch.url; });
      setChannels(map);
    });
  }, []);

  const socialLinks = [
    { key: "whatsapp", icon: MessageCircle, label: "واتساب", fallback: "https://wa.me/message/2YZPI7YOJPW4D1" },
    { key: "instagram", icon: Instagram, label: "انستغرام", fallback: "https://www.instagram.com/abulailafamily" },
    { key: "facebook", icon: Facebook, label: "فيسبوك", fallback: "https://facebook.com/" },
    { key: "twitter_x", icon: Twitter, label: "تويتر", fallback: "https://twitter.com/" },
  ];

  const navLinks = [
    { to: "/", label: "الرئيسية" },
    { to: "/about", label: "عن العائلة" },
    { to: "/businesses", label: "شركات العائلة" },
    { to: "/gallery", label: "المعرض" },
    { to: "/news", label: "الأخبار" },
    { to: "/tree", label: "شجرة العائلة" },
  ];

  return (
    <footer className="relative z-50 border-t border-primary/10 bg-gradient-to-b from-card/50 to-background mt-20">
      {/* Decorative Top Border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      <div className="container mx-auto px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          {/* Brand Section */}
          <div className="text-center md:text-start">
            <Link to="/" className="inline-flex items-center gap-3 group mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/20 group-hover:shadow-primary/40 transition-shadow">
                <Sparkles className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex flex-col items-start">
                <span className="font-amiri text-2xl gold-text font-bold">عائلة أبوليلة</span>
                <span className="text-xs text-muted-foreground">حفظ التراث والأنساب</span>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-xs font-tajawal">
              نحفظ تاريخ عائلتنا وأنسابنا ونوثق روابطنا العائلية للأجيال القادمة
            </p>
          </div>

          {/* Navigation Links */}
          <div className="text-center">
            <h4 className="font-amiri text-lg text-primary mb-4">روابط سريعة</h4>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              {navLinks.map((link) => (
                <Link 
                  key={link.to} 
                  to={link.to} 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors font-tajawal"
                  data-testid={`footer-link-${link.to.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Social Links */}
          <div className="text-center md:text-end">
            <h4 className="font-amiri text-lg text-primary mb-4">تواصل معنا</h4>
            <div className="flex justify-center md:justify-end gap-3">
              {socialLinks.map(({ key, icon: Icon, label, fallback }) => {
                const url = channels[key] || fallback;
                return (
                  <motion.a 
                    key={key} 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    aria-label={label}
                    data-testid={`footer-social-${key}`}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    className="h-11 w-11 rounded-xl bg-gradient-to-br from-secondary/80 to-secondary/40 flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 border border-primary/10 hover:border-primary/30"
                  >
                    <Icon className="h-5 w-5" />
                  </motion.a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent mb-6" />

        {/* Credits & Copyright */}
        <div className="text-center space-y-3">
          <p className="text-muted-foreground text-sm font-tajawal flex items-center justify-center gap-2 flex-wrap">
            <span>تم إنجاز هذا العمل بواسطة</span>
            <span className="text-foreground font-semibold">سعود سلطان علي محمد أبوليلة آل علي</span>
            <span>&</span>
            <span className="text-foreground font-semibold">عبدالله سيف علي محمد أبوليلة آل علي</span>
          </p>
          <p className="text-muted-foreground/60 text-xs font-tajawal flex items-center justify-center gap-2">
            <span>© {new Date().getFullYear()} عائلة أبوليلة</span>
            <span>—</span>
            <span>جميع الحقوق محفوظة</span>
            <Heart className="w-3 h-3 text-primary/50 fill-primary/30" />
          </p>
        </div>
      </div>
    </footer>
  );
}
